/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import mongoose from "mongoose";

import { withStyles, createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import { Tab, Tabs } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

import cssStyles from "./presentialPaymentSettings.module.css";

import { getAllPayments as getPayments, updatePayment } from "../../../../reducers/paymentSlice";
import paymentsConstants from "../../../../constants/payments.constants";
import { Notification } from "../../../../containers/notification";
import { selectUser } from "../../../../reducers/userSlice";
import { loadingOff, loadingOn, selectLoadingUI } from "../../../../reducers/uiSlice";
import { selectCompany, getCompany } from "../../../../reducers/companySlice";
import { selectSubsidiaries } from "../../../../reducers/subsidiarySlice";
import { selectChangesDeposit, setChanges } from "../../../../reducers/depositSlice";
import { useChangeRecord } from "../../../../utils/hooks/useChangeRecord";
import companyService from "../../../../utils/services/company";
import paymentsService from "../../../../utils/services/payments";
import dateUtils from "../../../../utils/date";
import {
  CheckCircleGreenIcon,
  CloseCircleGrayIcon,
  CreditCardIcon,
  MoneyBillIcon,
} from "../../../../assets/icons/svgIcons";

const GLOBAL_KEY = "global";

const METHODS = [
  {
    key: "cash",
    name: paymentsConstants.TYPES.Cash,
    label: "Efectivo",
    flag: "useCash",
    changeText: "Usar método de pago Efectivo",
  },
  {
    key: "card",
    name: paymentsConstants.TYPES.Card,
    label: "Tarjeta (presencial)",
    flag: "useCard",
    changeText: "Usar método de pago Tarjeta presencial",
  },
];

const defaultRow = (company, name, subsidiaryId) => ({
  name,
  factor: 1,
  duration: 60,
  startTime: "01:00",
  endTime: "23:59",
  details: [],
  useSandbox: false,
  _id: new mongoose.Types.ObjectId().toHexString(),
  IdCompany: company._id,
  subsidiaryId,
});

function PresentialPaymentSettings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { recordChange } = useChangeRecord();

  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const subsidiaries = useSelector(selectSubsidiaries);
  const changes = useSelector(selectChangesDeposit);
  const isLoading = useSelector(selectLoadingUI);

  const [enabledMap, setEnabledMap] = useState({});
  const [updated, setUpdated] = useState(false);
  const [showInputs, setShowInputs] = useState(false);
  const [subsidiaryId, setSubsidiaryId] = useState("");

  const perSubsidiary = Boolean(company?.useSubsidiaryPayment);

  const branchKeys = useMemo(
    () => (perSubsidiary ? (subsidiaries || []).map((item) => item._id) : [GLOBAL_KEY]),
    [perSubsidiary, subsidiaries]
  );

  const rows = useMemo(() => {
    const names = METHODS.map((item) => item.name);
    return (company?.Payments || []).filter((item) => names.includes(item.name));
  }, [company?.Payments]);

  const findRow = (name, branchKey) =>
    rows.find((item) =>
      branchKey === GLOBAL_KEY
        ? item.name === name && !item.subsidiaryId
        : item.name === name && item.subsidiaryId === branchKey
    );

  const activeKey = perSubsidiary && subsidiaryId ? subsidiaryId : GLOBAL_KEY;
  const currentEnabled = enabledMap[activeKey] || {};
  const anyEnabled = METHODS.some((method) =>
    branchKeys.some((branchKey) => enabledMap[branchKey]?.[method.key])
  );

  useEffect(() => {
    if (perSubsidiary && subsidiaries?.length > 0 && subsidiaries[0]?._id) {
      setSubsidiaryId(subsidiaries[0]._id);
    }
  }, [perSubsidiary, subsidiaries]);

  useEffect(() => {
    const next = {};
    branchKeys.forEach((branchKey) => {
      next[branchKey] = METHODS.reduce((acc, method) => {
        acc[method.key] = Boolean(findRow(method.name, branchKey));
        return acc;
      }, {});
    });
    setEnabledMap(next);
    setUpdated(false);
  }, [rows, branchKeys.join("|")]);

  const handleToggle = (methodKey, checked) => {
    setEnabledMap((prev) => ({
      ...prev,
      [activeKey]: { ...(prev[activeKey] || {}), [methodKey]: checked },
    }));
    setUpdated(true);
  };

  const trackChange = (method, newValue) => {
    const newChanges = [...changes];
    const change = {
      text: method.changeText,
      oldValue: Boolean(company?.UsePayments?.[method.flag]),
      newValue,
      date: dateUtils.getUTC().slice(0, 19).replace("T", " "),
    };
    const index = newChanges.findIndex((item) => item.text === change.text);
    if (index !== -1) {
      change.oldValue = newChanges[index].oldValue;
      newChanges[index] = change;
    } else {
      newChanges.push(change);
    }
    dispatch(setChanges(newChanges));
  };

  const handleCancel = () => {
    getPayments(company._id, user.token, dispatch);
    setUpdated(false);
  };

  const handleSave = async () => {
    try {
      dispatch(loadingOn());

      const operations = [];
      branchKeys.forEach((branchKey) => {
        METHODS.forEach((method) => {
          const row = findRow(method.name, branchKey);
          const wanted = Boolean(enabledMap[branchKey]?.[method.key]);
          if (wanted && !row) {
            operations.push(
              updatePayment(
                defaultRow(company, method.name, branchKey === GLOBAL_KEY ? "" : branchKey),
                user.token
              )
            );
          } else if (!wanted && row) {
            operations.push(paymentsService.deletePaymentCredentials(row._id, user.token));
          }
        });
      });

      await Promise.all(operations);

      const usePayments = { ...company.UsePayments };
      METHODS.forEach((method) => {
        usePayments[method.flag] = branchKeys.some(
          (branchKey) => enabledMap[branchKey]?.[method.key]
        );
      });

      await companyService.updateCompany(
        company._id,
        { _id: company._id, UsePayments: usePayments },
        user.token
      );

      await getCompany(company._id, dispatch);
      await getPayments(company._id, user.token, dispatch);

      recordChange({
        module: "Métodos de pago",
        submodule: "Pagos presenciales",
        subsidiaryId: activeKey,
        changes,
      });

      setUpdated(false);
      enqueueSnackbar(
        Notification({
          text: "Métodos de pago actualizados correctamente",
          variant: "success",
          withDetails: false,
        })
      );
    } catch (error) {
      console.error("Error saving presential payment methods:", error);
      enqueueSnackbar(
        Notification({
          text: "Error al actualizar métodos de pago",
          variant: "error",
          withDetails: false,
        })
      );
    } finally {
      dispatch(loadingOff());
    }
  };

  return (
    <MuiThemeProvider theme={theme}>
      <div className={cssStyles.parentContainer}>
        <button className={cssStyles.backButton} onClick={() => navigate(-1)}>
          <span>
            <ArrowBackIcon />
          </span>
          Regresar
        </button>

        <div className={cssStyles.infoContainer}>
          <div className={cssStyles.titleLogoContainer}>
            <div className={cssStyles.containerLogo}>
              <MoneyBillIcon />
              <CreditCardIcon />
              <p className={cssStyles.title}>Pagos presenciales</p>
            </div>
            <div className={cssStyles.serviceStatusContainer}>
              <div className={cssStyles.containerLogo}>
                {anyEnabled ? <CheckCircleGreenIcon /> : <CloseCircleGrayIcon />}
                {anyEnabled ? "Activo" : "Inactivo"}
              </div>
            </div>
          </div>

          <div className={cssStyles.infoItemContainer}>
            <p className={cssStyles.infoItemTitle}>
              Permite a tus clientes pagar al momento de recibir o retirar el pedido
            </p>
            <p className={cssStyles.infoItem}>
              Solo se ofrecen en el checkout cuando el pedido es con envío propio o retiro en el
              local. El tipo de tarjeta (débito o crédito) se define al marcar la orden como pagada.
            </p>
          </div>

          <button className={cssStyles.buttonConfig} onClick={() => setShowInputs((prev) => !prev)}>
            Configurar
            <span>{!showInputs ? <ExpandMoreIcon /> : <ExpandLessIcon />}</span>
          </button>

          {showInputs && perSubsidiary && (
            <Tabs
              value={subsidiaryId}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_, newValue) => setSubsidiaryId(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {subsidiaries.map((item) => (
                <Tab label={item.Name} value={item._id} key={item._id} />
              ))}
            </Tabs>
          )}

          {showInputs && (
            <div className={cssStyles.configSettingsContainer}>
              <p className={cssStyles.scopeHint}>
                {perSubsidiary
                  ? "Elegí en qué sucursales se aceptan estos métodos."
                  : "Se aplican a todas las sucursales. Para configurarlos por sucursal, activá el pago por sucursal en la configuración de la tienda."}
              </p>

              {METHODS.map((method) => (
                <div className={cssStyles.inputContainer} key={method.key}>
                  <label className={cssStyles.labelInput}>
                    {method.key === "cash" ? <MoneyBillIcon /> : <CreditCardIcon />}
                    {method.label}
                  </label>
                  <Checkbox
                    style={{ color: "#122526", margin: "0px 5px", padding: 0 }}
                    checked={Boolean(currentEnabled[method.key])}
                    onChange={(event) => {
                      handleToggle(method.key, event.target.checked);
                      trackChange(method, event.target.checked);
                    }}
                    value={String(Boolean(currentEnabled[method.key]))}
                    color="primary"
                    id={`use-${method.key}`}
                  />
                </div>
              ))}
            </div>
          )}

          {showInputs && (
            <div className={cssStyles.buttonsContainer}>
              <button
                className={cssStyles.cancelButton}
                disabled={!updated || isLoading}
                onClick={handleCancel}
              >
                Cancelar
              </button>
              <button
                className={cssStyles.confirmButton}
                disabled={!updated || isLoading}
                onClick={handleSave}
              >
                Guardar
              </button>
            </div>
          )}
        </div>
      </div>
    </MuiThemeProvider>
  );
}

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
});

const theme = createTheme({
  typography: {
    useNextVariants: true,
    fontFamily: "Roboto, sans-serif !important",
  },
  palette: {
    primary: { main: "#122526" },
  },
  overrides: {
    MuiFormLabel: {
      root: { color: "#122526 !important" },
    },
    MuiFormControl: {
      root: { width: 100, margin: "0px 5px" },
    },
  },
});

export default withStyles(styles)(PresentialPaymentSettings);
